import { useMemo, useState } from 'react';
import { formatearCLP, formatearCLPCompacto, formatearPorcentajeSimple, type PuntoCurva } from '@lexicore/shared';

const ANCHO = 900;
const ALTO = 300;
const MARGEN = { izquierda: 58, derecha: 20, arriba: 30, abajo: 50 };

/**
 * Curva precio → probabilidad de ganar.
 *
 * Es la pieza que cambia cómo se decide un precio. El motor anterior entregaba
 * un número; ofertar no es adivinar el número correcto, es elegir un punto en
 * una curva: más barato, más probable ganar y menos margen queda. Esa decisión
 * es del dueño. Lo que el sistema puede hacer es mostrarle la curva completa —
 * incluido el punto donde la utilidad esperada es máxima, que casi nunca
 * coincide con el precio más probable de ganar.
 */
export function CurvaProbabilidad({
  curva,
  pisoPorMargen,
  precioSugerido,
  precioElegido,
  alElegir,
  modalidadMonto = 'NETO',
}: {
  curva: PuntoCurva[];
  pisoPorMargen: number | null;
  precioSugerido: number;
  precioElegido: number | null;
  alElegir: (precio: number) => void;
  modalidadMonto?: 'NETO' | 'BRUTO';
}) {
  const [sobre, setSobre] = useState<number | null>(null);

  const { escalaX, escalaY, trazo, indiceOptimo, utilidadMaxima } = useMemo(() => {
    const precios = curva.map((punto) => punto.precio);
    const min = Math.min(...precios);
    const max = Math.max(...precios);
    const rango = max - min || 1;

    const escalaX = (precio: number) =>
      MARGEN.izquierda + ((precio - min) / rango) * (ANCHO - MARGEN.izquierda - MARGEN.derecha);
    const escalaY = (probabilidad: number) =>
      ALTO - MARGEN.abajo - (probabilidad / 100) * (ALTO - MARGEN.arriba - MARGEN.abajo);

    const utilidades = curva.map((punto) => punto.utilidadEsperada ?? 0);
    const utilidadMaxima = Math.max(...utilidades, 0);
    const indiceOptimo = utilidadMaxima > 0 ? utilidades.indexOf(utilidadMaxima) : -1;

    const trazo = curva
      .map((punto, i) => `${i === 0 ? 'M' : 'L'}${escalaX(punto.precio).toFixed(1)} ${escalaY(punto.probabilidadPct).toFixed(1)}`)
      .join(' ');

    return { escalaX, escalaY, trazo, indiceOptimo, utilidadMaxima };
  }, [curva]);

  const indiceActivo = useMemo(() => {
    if (sobre !== null) return sobre;
    const objetivo = precioElegido ?? precioSugerido;
    // El punto más cercano al precio elegido: la curva viene muestreada, así que
    // rara vez cae exactamente encima.
    let mejor = 0;
    let distancia = Infinity;
    curva.forEach((punto, i) => {
      const d = Math.abs(punto.precio - objetivo);
      if (d < distancia) {
        distancia = d;
        mejor = i;
      }
    });
    return mejor;
  }, [sobre, precioElegido, precioSugerido, curva]);

  const activo = curva[indiceActivo];
  const optimo = indiceOptimo >= 0 ? curva[indiceOptimo] : null;

  const etiquetasX = useMemo(() => {
    const paso = Math.max(1, Math.floor(curva.length / 6));
    return curva.filter((_, i) => i % paso === 0);
  }, [curva]);

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4, gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
          Precio y probabilidad de ganar
        </h3>
        <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
          Toca un punto para evaluarlo · {modalidadMonto === 'BRUTO' ? 'Eje en Bruto (con IVA)' : 'Eje en Neto'}
        </span>
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.5, maxWidth: 720 }}>
        Estimada sobre los precios con que se ganaron procesos comparables: en qué proporción de ellos tu precio habría
        bastado. Las barras muestran la utilidad esperada — probabilidad por margen.
      </p>

      <svg viewBox={`0 0 ${ANCHO} ${ALTO}`} style={{ width: '100%', height: 300, display: 'block' }} role="img" aria-label="Curva de precio contra probabilidad de ganar">
        {[0, 25, 50, 75, 100].map((valor) => (
          <g key={valor}>
            <line x1={MARGEN.izquierda} y1={escalaY(valor)} x2={ANCHO - MARGEN.derecha} y2={escalaY(valor)} stroke="var(--border-subtle)" strokeWidth={1} />
            <text x={MARGEN.izquierda - 8} y={escalaY(valor) + 3.5} fontFamily="var(--font-mono)" fontSize={9.5} fill="var(--text-muted)" textAnchor="end">
              {valor}%
            </text>
          </g>
        ))}

        {/*
          La clave es el índice y no el precio a propósito: con presupuestos
          chicos, dos ratios consecutivos de la curva redondean al mismo peso y
          producirían claves duplicadas —React omite elementos cuando eso pasa—.
          La curva es un arreglo posicional que nunca se reordena ni se filtra,
          que es justo el caso donde el índice es la clave correcta.
        */}
        {utilidadMaxima > 0 &&
          curva.map((punto, i) => {
            const alto = ((punto.utilidadEsperada ?? 0) / utilidadMaxima) * 96;
            return (
              <rect
                key={`barra-${i}`}
                x={escalaX(punto.precio) - 9}
                y={ALTO - MARGEN.abajo - alto}
                width={18}
                height={Math.max(0, alto)}
                rx={2}
                fill={i === indiceOptimo ? 'rgba(0,200,83,.30)' : 'rgba(120,144,156,.16)'}
              />
            );
          })}

        {pisoPorMargen !== null && pisoPorMargen >= curva[0]!.precio && pisoPorMargen <= curva[curva.length - 1]!.precio && (
          <g>
            <line
              x1={escalaX(pisoPorMargen)}
              y1={MARGEN.arriba - 6}
              x2={escalaX(pisoPorMargen)}
              y2={ALTO - MARGEN.abajo + 6}
              stroke="var(--accent-red)"
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
            <text x={escalaX(pisoPorMargen)} y={MARGEN.arriba - 12} fontSize={9.5} fontWeight={600} fill="var(--accent-red)" textAnchor="middle">
              piso de margen (neto)
            </text>
          </g>
        )}

        <path d={trazo} fill="none" stroke="var(--accent-blue)" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />

        {activo && (
          <line
            x1={escalaX(activo.precio)}
            y1={MARGEN.arriba - 6}
            x2={escalaX(activo.precio)}
            y2={ALTO - MARGEN.abajo + 6}
            stroke="var(--accent-blue)"
            strokeWidth={1}
            strokeDasharray="3 3"
            opacity={0.5}
          />
        )}

        {curva.map((punto, i) => (
          <g
            key={`punto-${i}`}
            style={{ cursor: 'pointer' }}
            onClick={() => alElegir(punto.precio)}
            onMouseEnter={() => setSobre(i)}
            onMouseLeave={() => setSobre(null)}
          >
            <circle cx={escalaX(punto.precio)} cy={escalaY(punto.probabilidadPct)} r={14} fill="transparent" />
            <circle
              cx={escalaX(punto.precio)}
              cy={escalaY(punto.probabilidadPct)}
              r={i === indiceActivo ? 6.5 : 3.5}
              fill={i === indiceActivo ? 'var(--accent-blue)' : 'var(--bg-card)'}
              stroke="var(--accent-blue)"
              strokeWidth={2.5}
            />
          </g>
        ))}

        {etiquetasX.map((punto, i) => {
          const valorEje = modalidadMonto === 'BRUTO'
            ? (punto.precioBruto ?? Math.round(punto.precio * 1.19))
            : (punto.precioNeto ?? punto.precio);
          return (
            <text
              key={`eje-${i}`}
              x={escalaX(punto.precio)}
              y={ALTO - MARGEN.abajo + 22}
              fontSize={9.5}
              fill="var(--text-muted)"
              textAnchor="middle"
            >
              {formatearCLPCompacto(valorEje)}
            </text>
          );
        })}
      </svg>

      {activo && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 28,
            flexWrap: 'wrap',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--accent-blue)',
            borderRadius: 'var(--radius-sm)',
            padding: '14px 18px',
            marginTop: 14,
          }}
        >
          <div>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
              {modalidadMonto === 'BRUTO' ? 'Si ofertas (Bruto con IVA)' : 'Si ofertas (Neto)'}
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 21, fontWeight: 800, marginTop: 3, color: 'var(--accent-blue)' }}>
              {modalidadMonto === 'BRUTO'
                ? formatearCLP(activo.precioBruto ?? Math.round(activo.precio * 1.19))
                : formatearCLP(activo.precioNeto ?? activo.precio)}
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
              {modalidadMonto === 'BRUTO'
                ? `Equivale a ${formatearCLP(activo.precioNeto ?? activo.precio)} Neto`
                : `Con 19% IVA: ${formatearCLP(activo.precioBruto ?? Math.round(activo.precio * 1.19))}`}
            </div>
          </div>
          <Separador />
          <Lectura
            etiqueta="Probabilidad de ganar"
            valor={formatearPorcentajeSimple(activo.probabilidadPct)}
            color={activo.probabilidadPct >= 60 ? 'var(--accent-green)' : activo.probabilidadPct >= 35 ? 'var(--accent-yellow)' : 'var(--accent-red)'}
          />
          {activo.utilidadEsperada !== null && (
            <>
              <Separador />
              <Lectura etiqueta="Utilidad esperada (Neto)" valor={formatearCLP(activo.utilidadEsperada)} />
            </>
          )}
        </div>
      )}

      {optimo && optimo.precio !== precioSugerido && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
          El máximo de utilidad esperada cae en{' '}
          <strong style={{ color: 'var(--accent-green)', fontFamily: 'var(--font-mono)' }}>
            {formatearCLP(optimo.precioNeto ?? optimo.precio)} Neto ({formatearCLP(optimo.precioBruto ?? Math.round(optimo.precio * 1.19))} con IVA)
          </strong>, no en el
          precio sugerido. La diferencia es real: ofertar más barato sube la probabilidad más rápido de lo que baja el
          margen, o al revés.
        </div>
      )}
    </div>
  );
}

function Lectura({ etiqueta, valor, color }: { etiqueta: string; valor: string; color?: string }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{etiqueta}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 21, fontWeight: 800, marginTop: 3, color: color ?? 'var(--text-primary)' }}>
        {valor}
      </div>
    </div>
  );
}

const Separador = () => <div style={{ width: 1, height: 36, background: 'var(--border-medium)' }} />;
